import { Button } from '@components/ui/button';
import { Calendar } from '@components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { cn } from '@lib/utils';
import { format } from 'date-fns';
import { CalendarIcon, XIcon } from 'lucide-react';
import { FC, useState } from 'react';

/**
 * DatePicker value is an ISO date string (YYYY-MM-DD)
 */
type DatePickerValue = string;

type DatePickerProps = {
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  id?: string;
  onChange: (value: DatePickerValue | null) => void;
  placeholder?: string;
  value?: DatePickerValue | null;
};

/**
 * Converts ISO date string (YYYY-MM-DD) to Date object for Calendar display
 */
function isoDateToDate(isoDate: string | null | undefined): Date | undefined {
  if (!isoDate) return undefined;
  // Parse ISO date string and use local date for Calendar display
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Converts Date object to ISO date string (YYYY-MM-DD)
 */
function dateToIsoDate(date: Date | undefined): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const DatePicker: FC<DatePickerProps> = (props) => {
  const { className, clearable = false, disabled, hasError, id, onChange, placeholder = 'Select date', value } = props;
  const ariaInvalid = hasError;

  const [open, setOpen] = useState(false);

  const selectedDate = isoDateToDate(value);

  const handleSelect = (date: Date | undefined) => {
    onChange(dateToIsoDate(date));
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const displayValue = selectedDate ? format(selectedDate, 'MMM d, yyyy') : placeholder;
  const hasValue = !!value;

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          aria-invalid={ariaInvalid}
          className={cn(
            'w-full justify-between font-normal',
            !hasValue && 'text-muted-foreground',
            'focus-visible:border-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300',
            'aria-invalid:border-red-500 aria-invalid:focus-visible:border-red-500 aria-invalid:focus-visible:ring-red-200',
            className,
          )}
          disabled={disabled}
          id={id}
          type="button"
          variant="outline"
        >
          <span className="flex items-center gap-2">
            <CalendarIcon className="size-4" />
            {displayValue}
          </span>
          {clearable && hasValue && (
            <span
              className="ml-auto flex size-4 items-center justify-center rounded-full hover:bg-gray-200"
              onClick={handleClear}
              onKeyDown={(e) => e.key === 'Enter' && handleClear(e as unknown as React.MouseEvent)}
              role="button"
              tabIndex={0}
            >
              <XIcon className="size-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto overflow-hidden p-0"
      >
        <Calendar
          captionLayout="dropdown"
          defaultMonth={selectedDate}
          mode="single"
          onSelect={handleSelect}
          selected={selectedDate}
        />
      </PopoverContent>
    </Popover>
  );
};

export type { DatePickerValue };
